import { useContext, useEffect, useRef } from "react";

import { StateContext } from "../../context/globalContext/context";
import { iGlobalContext } from "../../context/globalContext/interface";
import { drainPendingEvents } from "./pendingTheresiasQueue";
import { postIntentEvent } from "./postIntentEvent";

/**
 * Drains the pre-login Theresias event buffer once login completes, then
 * re-fires each entry through the normal transport with the now-known
 * `accessJWT` + `userInfo.id`. Original `context.ts` (the client epoch ms
 * captured at fire-time) is preserved verbatim, so backend sees correct
 * event-time despite the deferred network call.
 *
 * Mount ONCE at App root (App.tsx — next to `useDeadClickTracking`,
 * `useScrollDepthTracking`, `useBrowserLifecycleTracking`). Watches
 * `accessJWT` + `userInfo.id` from StateContext. When both transition to
 * truthy AND we haven't flushed yet this session → drain + sequentially
 * replay. The sequential `await` preserves the onboarding funnel order
 * (budget → travel → vibe) on the backend ingest side.
 *
 * `flushedThisSessionRef` resets on logout (both values fall back to falsy),
 * so a subsequent login flushes its own queued events without contamination.
 *
 * `ownerEmail` is the only field re-enriched at replay time — it was empty
 * pre-login because `userInfo.email` wasn't known. `chatId`, `itineraryIndex`,
 * and any other bridge-enriched fields stay as-queued for state-at-fire-time
 * accuracy (re-enriching against current state would mislead the graph).
 */
export function useFlushPendingTheresias(): void {
  const { accessJWT, userInfo } = useContext(StateContext) as iGlobalContext;
  const flushedThisSessionRef = useRef(false);

  useEffect(() => {
    const uid = userInfo?.id ? String(userInfo.id) : "";
    const hasAuth = !!accessJWT && !!uid;

    // Logout transition (both fell to falsy) — reset the guard so the next
    // login will flush its own buffered events.
    if (!hasAuth) {
      flushedThisSessionRef.current = false;
      return;
    }

    if (flushedThisSessionRef.current) return;
    flushedThisSessionRef.current = true;

    // Async IIFE — useEffect can't be async directly, and we want to await
    // each fire sequentially for funnel-order preservation. Errors swallowed
    // per the existing fire-and-forget contract; never break a user flow.
    (async () => {
      const entries = drainPendingEvents();
      if (entries.length === 0) return;

      const ownerEmail = userInfo?.email;
      for (const entry of entries) {
        const queuedCustom = (entry.custom ?? {}) as Record<string, unknown>;
        const enrichedCustom: Record<string, unknown> = { ...queuedCustom };

        // Backfill ownerEmail only when the queued payload didn't already
        // carry one (pre-login enrichment couldn't supply it). Leaves
        // event_uuid + chatId + itineraryIndex untouched — they reflect the
        // state at the original fire moment.
        if (!("ownerEmail" in enrichedCustom) && ownerEmail) {
          enrichedCustom.ownerEmail = ownerEmail;
        }
        // event_uuid lives inside `custom` so backend dedup catches the
        // multi-tab double-drain race.
        if (!("event_uuid" in enrichedCustom)) {
          enrichedCustom.event_uuid = entry.event_uuid;
        }

        try {
          await postIntentEvent(
            accessJWT,
            uid,
            entry.event_name,
            enrichedCustom,
            entry.context
          );
        } catch {
          // postIntentEvent already swallows network errors; this catch is a
          // defense-in-depth so one bad entry can't tank the rest of the loop.
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessJWT, userInfo?.id, userInfo?.email]);
}
